import React, { useContext, useRef, useMemo } from "react";
import "./chatCSS/Chat.css";
import { GlobalVariablesContext } from "../GlobalVariables";
import "react-perfect-scrollbar/dist/css/styles.css";
import "emoji-mart/css/emoji-mart.css"; // Import emoji-mart CSS
import Body from "./components/Body/Body";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import axios from "axios";
import toast from "react-hot-toast";

const SWIPE_THRESHOLD = 60; // minimum px horizontal drag to count as a swipe

function Chat() {
  const {
    currentUser,
    setCurrentUser,
    rooms,
    selectedPlatform,
    setIsMobileSidebarHidden,
  } = useContext(GlobalVariablesContext);

  // Determine platform class
  const platformClass = currentUser?.platform
    ? `chat-bg-${currentUser.platform}`
    : '';

  // --- Build the filtered navigation list that matches exactly what the sidebar shows ---
  // This mirrors the same filter logic in Sidebar.jsx so swipe always stays within the
  // same platform bucket the user has selected.
  const navRooms = useMemo(() => {
    if (!rooms || rooms.length === 0) return [];
    if (selectedPlatform === 'all') return rooms;
    return rooms.filter((r) => r.platform === selectedPlatform);
  }, [rooms, selectedPlatform]);

  // --- Swipe navigation (mobile only) ---
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    // Only handle swipe on small screens where the sidebar is hidden
    if (window.innerWidth >= 700) return;
    if (touchStartX.current === null) return;

    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    // Reset refs immediately so no accidental re-trigger
    touchStartX.current = null;
    touchStartY.current = null;

    // Ignore if the gesture is more vertical than horizontal (user is scrolling)
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    // Ignore gestures that don't meet the minimum distance threshold
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    if (!currentUser || navRooms.length === 0) return;

    // Robust ID comparison: compare as strings to avoid type mismatch
    // (WhatsApp IDs are numbers from Django; Meta IDs are strings like "user_business")
    const currentId = String(currentUser.conversation_id ?? "");
    const currentIndex = navRooms.findIndex(
      (r) => String(r.id ?? "") === currentId
    );

    if (currentIndex === -1) {
      // Current chat not found in the filtered list — this can happen when the
      // user switched platforms after opening a chat. Show a helpful message.
      toast("Switch platform filter to navigate", {
        duration: 1800,
        style: { fontSize: "13px" },
      });
      return;
    }

    // Swipe left → next chat (higher index = older/lower in list)
    // Swipe right → previous chat (lower index = newer/higher in list)
    const targetIndex = deltaX < 0 ? currentIndex + 1 : currentIndex - 1;

    if (targetIndex < 0 || targetIndex >= navRooms.length) {
      // Already at the boundary — give subtle feedback
      toast(deltaX < 0 ? "No more chats ›" : "‹ No more chats", {
        duration: 1200,
        style: { fontSize: "13px" },
      });
      return;
    }

    const targetRoom = navRooms[targetIndex];

    // Navigate to the target room
    setCurrentUser({
      name: targetRoom.name,
      text: targetRoom.text,
      phone: targetRoom.phone,
      wb_num: targetRoom.whatsapp_bussiness_number,
      platform: targetRoom.platform,
      conversation_id: targetRoom.id,
      profile_picture: targetRoom.profile_picture,
      business_user_id: targetRoom.business_user_id,
      handover_data: targetRoom.handover_data,
      original_data: targetRoom.original_data,
    });

    // Mark as read for Meta platforms
    if (
      (targetRoom.platform === "instagram" || targetRoom.platform === "facebook") &&
      targetRoom.id
    ) {
      axios
        .post(`https://jutiyapa.com/api/conversations/${targetRoom.id}/mark-as-read/`)
        .catch((err) => console.error("Error marking read on swipe:", err));
    }

    // Keep sidebar hidden on mobile when navigating between chats
    if (window.innerWidth < 700) {
      setIsMobileSidebarHidden(true);
    }

    const direction = deltaX < 0 ? "›" : "‹";
    toast(`${direction} ${targetRoom.name}`, {
      duration: 1500,
      style: { fontSize: "13px" },
    });
  };

  return (
    <div
      className="Chat"
      style={{ touchAction: "pan-y" }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="Chat__header">
        <Header />
      </div>

      <div className={`Chat__bodyOfMsg ${platformClass}`}>
        <Body />
      </div>

      <div className="Chat__footer">
        <Footer />
      </div>
    </div>
  );
}

export default Chat;
